import { FormlyFieldConfig } from '@ngx-formly/core';
import { ReportColumn, ReportDefinition } from '../models/report.model';

/**
 * Onboarding sample scenarios: ready-made form + checkpoints + saved reports
 * for a specific use case, seeded into a new org so the user can try a real
 * scan → submit → report loop before building anything themselves.
 *
 * This file is pure data. Seeding/removal lives in SampleScenarioService and
 * the guided tour in sample-scenario-tour.ts.
 *
 * The seeded content (form/checkpoint/report names, field labels) is *user
 * data*, written once in English like anything a user would type — it is not
 * UI copy, so it isn't in en.json. The scenario picker's titles/descriptions
 * ARE UI copy and use i18n keys.
 *
 * Field keys are stable, readable ids (not the builder's UUIDs) so the sample
 * reports below can reference them. The form builder treats any key as the
 * field id, so these forms stay fully editable.
 */

export type SampleScenarioId = 'patrol' | 'janitorial' | 'hvac';

export const SAMPLE_SCENARIO_IDS: readonly SampleScenarioId[] = ['patrol', 'janitorial', 'hvac'];

export interface SampleCheckpointSpec {
  name: string;
  description?: string;
  /** Serial numbers of numbered copies (asset instances), e.g. one per HVAC unit. */
  instances?: string[];
}

export interface SampleFormSpec {
  name: string;
  fields: FormlyFieldConfig[];
  columns?: 1 | 2;
  auto_submit?: boolean;
  play_success_sound?: boolean;
}

/** Ids of the seeded rows a report definition needs to reference. */
export interface SampleReportContext {
  formId: string;
}

export interface SampleScenarioDefinition {
  id: SampleScenarioId;
  /** Lucide icon name for the picker card. */
  icon: string;
  /** i18n keys (picker card). */
  titleKey: string;
  descriptionKey: string;
  /** i18n keys: bullet list of what the scenario demonstrates. */
  highlightKeys: string[];
  form: SampleFormSpec;
  checkpoints: SampleCheckpointSpec[];
  reports: (ctx: SampleReportContext) => Omit<ReportDefinition, 'visibility'>[];
}

// ---------------------------------------------------------------------------
// Field + report helpers (keep the stored shape identical to the form builder's
// fieldDefToFormly() output, minus the runtime-only validator functions).
// ---------------------------------------------------------------------------

function textLabel(key: string, content: string): FormlyFieldConfig {
  return {
    key,
    id: key,
    type: 'text-label',
    className: 'flex-basis-full',
    props: {
      label: '',
      required: false,
      content,
      fontSize: '1rem',
      fontWeight: 'normal',
      textAlign: 'left',
      tag: 'div',
    },
  };
}

function options(labels: string[]): { label: string; value: string }[] {
  return labels.map((label) => ({ label, value: label }));
}

/** Shown only while another field holds one of `values` (form builder "visible when"). */
interface VisibleWhen {
  fieldId: string;
  values: string[];
}

function checklist(
  key: string,
  label: string,
  items: string[],
  visibleWhen?: VisibleWhen,
): FormlyFieldConfig {
  return {
    key,
    id: key,
    type: 'checkbox-group',
    className: 'flex-basis-full',
    props: {
      label,
      required: false,
      options: options(items),
      multiple: true,
      optionsSource: 'inline',
      ...(visibleWhen ? { visibleWhen } : {}),
    },
  };
}

function field(
  key: string,
  type: string,
  label: string,
  props: Record<string, unknown> = {},
  fullWidth = true,
): FormlyFieldConfig {
  return {
    key,
    id: key,
    type,
    ...(fullWidth ? { className: 'flex-basis-full' } : {}),
    props: { label, placeholder: '', required: false, ...props },
  };
}

const CREATED_AT: ReportColumn = { source: 'metadata', key: 'created_at', fieldType: 'date' };
const CHECKPOINT: ReportColumn = { source: 'metadata', key: 'checkpoint', fieldType: 'text' };
const UNIT: ReportColumn = { source: 'metadata', key: 'asset_instance', fieldType: 'text' };
const RECORDED_BY: ReportColumn = { source: 'metadata', key: 'created_by', fieldType: 'text' };

function baseReport(
  name: string,
  description: string,
  formId: string,
): Omit<ReportDefinition, 'visibility'> {
  return {
    name,
    description,
    formScope: 'single_form',
    formId,
    timeRange: { preset: 'last_7_days' },
    metadataFilters: [],
    dynamicFilters: [],
    selectedColumns: [],
    sort: [{ source: 'metadata', key: 'created_at', direction: 'desc' }],
    aggregation: { mode: 'none' },
  };
}

function checkInsPerCheckpoint(
  name: string,
  description: string,
  formId: string,
): Omit<ReportDefinition, 'visibility'> {
  return {
    ...baseReport(name, description, formId),
    sort: [],
    aggregation: { mode: 'count' },
    groupBy: { source: 'metadata', key: 'checkpoint' },
  };
}

// ---------------------------------------------------------------------------
// Scenarios
// ---------------------------------------------------------------------------

/**
 * Security patrol: proof of presence. A zero-input form that saves itself the
 * moment the guard scans and chimes, so the guard never has to look at the
 * screen.
 */
const PATROL: SampleScenarioDefinition = {
  id: 'patrol',
  icon: 'shield-check',
  titleKey: 'onboarding.scenarios.patrol.title',
  descriptionKey: 'onboarding.scenarios.patrol.description',
  highlightKeys: [
    'onboarding.scenarios.patrol.highlight1',
    'onboarding.scenarios.patrol.highlight2',
    'onboarding.scenarios.patrol.highlight3',
  ],
  form: {
    name: 'Patrol Check-in',
    fields: [],
    auto_submit: true,
    play_success_sound: true,
  },
  checkpoints: [
    { name: 'Main Entrance', description: 'Patrol point: front lobby doors' },
    { name: 'Loading Dock', description: 'Patrol point: rear shipping doors' },
    { name: 'Parkade Level 2', description: 'Patrol point: stairwell door, P2' },
    { name: 'Server Room Door', description: 'Patrol point: 2nd floor, room 210' },
  ],
  reports: ({ formId }) => [
    {
      ...baseReport(
        'Patrol log (last 7 days)',
        'Every patrol check-in: where, when and by whom.',
        formId,
      ),
      selectedColumns: [CREATED_AT, CHECKPOINT, RECORDED_BY],
    },
    {
      ...checkInsPerCheckpoint(
        'Check-ins per patrol point (today)',
        'How many times each patrol point was visited today.',
        formId,
      ),
      timeRange: { preset: 'today' },
    },
  ],
};

const JANITORIAL_TASKS = [
  'Emptied trash and recycling',
  'Restocked paper towels, toilet paper and soap',
  'Cleaned toilets and urinals',
  'Cleaned sinks, counters and mirrors',
  'Mopped floor',
];

/** Janitorial: proof of service, plus problems reported on the spot with photos. */
const JANITORIAL: SampleScenarioDefinition = {
  id: 'janitorial',
  icon: 'spray-can',
  titleKey: 'onboarding.scenarios.janitorial.title',
  descriptionKey: 'onboarding.scenarios.janitorial.description',
  highlightKeys: [
    'onboarding.scenarios.janitorial.highlight1',
    'onboarding.scenarios.janitorial.highlight2',
    'onboarding.scenarios.janitorial.highlight3',
  ],
  form: {
    name: 'Restroom Service Record',
    play_success_sound: true,
    fields: [
      textLabel(
        'intro',
        '**{{checkpoint.title}}**: tick what you did, then note anything that needs attention.',
      ),
      checklist('tasks', 'Tasks completed', JANITORIAL_TASKS),
      field('condition', 'dropdown', 'Condition after service', {
        required: true,
        options: [
          { label: 'All good', value: 'ok' },
          { label: 'Needs attention', value: 'needs_attention' },
          { label: 'Out of order', value: 'out_of_order' },
        ],
        multiple: false,
        optionsSource: 'inline',
      }),
      checklist(
        'problems',
        'Problems found',
        [
          'Toilet clogged or overflowing',
          'Leak or standing water',
          'Supplies ran out before service',
          'Broken fixture (lock, dispenser, dryer)',
          'Vandalism or graffiti',
          'Other (see notes)',
        ],
        { fieldId: 'condition', values: ['needs_attention', 'out_of_order'] },
      ),
      field('notes', 'textarea', 'Notes', { rows: 3 }),
      field('photos', 'photo', 'Photos', { allowMultiplePhotos: true, filenamePrefix: '' }),
    ],
  },
  checkpoints: [
    { name: "Restroom 1st Floor (Men's)", description: 'Main building, beside the elevators' },
    { name: "Restroom 1st Floor (Women's)", description: 'Main building, beside the elevators' },
    { name: 'Restroom 2nd Floor (Accessible)', description: 'Main building, east wing' },
  ],
  reports: ({ formId }) => [
    {
      ...baseReport(
        'Restroom problems (last 7 days)',
        'Service visits where the restroom was left needing attention or out of order.',
        formId,
      ),
      dynamicFilters: [{ fieldKey: 'condition', fieldType: 'enum', operator: 'neq', value: 'ok' }],
      selectedColumns: [
        CREATED_AT,
        CHECKPOINT,
        { source: 'dynamic', key: 'condition', fieldType: 'enum', label: 'Condition' },
        { source: 'dynamic', key: 'problems', fieldType: 'enum', label: 'Problems found' },
        { source: 'dynamic', key: 'notes', fieldType: 'text', label: 'Notes' },
        RECORDED_BY,
      ],
    },
    checkInsPerCheckpoint(
      'Service visits per restroom (last 7 days)',
      'How often each restroom was serviced this week.',
      formId,
    ),
  ],
};

const HVAC_CHECKS = [
  'Replaced or cleaned air filters',
  'Inspected belts and pulleys',
  'Cleaned condenser coil',
  'Checked refrigerant pressures',
  'Cleared condensate drain',
  'Tested thermostat and controls',
  'Checked electrical connections',
];

/**
 * Equipment maintenance: an inspection checklist, a free-text work log, photos,
 * and links to the unit's manuals via markdown in a text-label field. One
 * checkpoint per model with a numbered copy per physical unit, so a single
 * label design covers every unit and reports can track each unit separately.
 */
const HVAC: SampleScenarioDefinition = {
  id: 'hvac',
  icon: 'wrench',
  titleKey: 'onboarding.scenarios.hvac.title',
  descriptionKey: 'onboarding.scenarios.hvac.description',
  highlightKeys: [
    'onboarding.scenarios.hvac.highlight1',
    'onboarding.scenarios.hvac.highlight2',
    'onboarding.scenarios.hvac.highlight3',
  ],
  form: {
    name: 'HVAC Preventive Maintenance',
    play_success_sound: true,
    fields: [
      textLabel(
        'unit_info',
        [
          '**Unit:** {{checkpoint.title}} {{checkpoint.instance}}',
          '',
          '📘 Documentation: [Installation & service manual](https://www.carrier.com/commercial/en/us/) · ' +
            '[Parts & filter sizes](https://www.carrier.com/commercial/en/us/)',
          '',
          '_Sample links: edit this text in the form builder to point at your own manuals._',
        ].join('\n'),
      ),
      checklist('checks', 'Inspection checklist', HVAC_CHECKS),
      field('supply_air_temp', 'number', 'Supply air temperature (°F)', {}, false),
      field('work_log', 'textarea', 'Maintenance log: work performed, parts used', { rows: 4 }),
      field('follow_up_required', 'checkbox', 'Follow-up required (unit needs further service)'),
      field(
        'follow_up_notes',
        'textarea',
        'What still needs to be done?',
        { rows: 2, visibleWhen: { fieldId: 'follow_up_required', values: ['true'] } },
      ),
      field('photos', 'photo', 'Photos', { allowMultiplePhotos: true, filenamePrefix: '' }),
    ],
  },
  checkpoints: [
    {
      name: 'Rooftop Unit (Carrier 48FC)',
      description: 'Packaged rooftop HVAC units, one label per unit',
      instances: ['RTU-1', 'RTU-2', 'RTU-3'],
    },
    { name: 'Server Room Split System', description: '2nd floor, room 210' },
  ],
  reports: ({ formId }) => [
    {
      ...baseReport(
        'HVAC service log (last 30 days)',
        'Every maintenance visit with the checklist, work performed and follow-up flag.',
        formId,
      ),
      timeRange: { preset: 'last_30_days' },
      selectedColumns: [
        CREATED_AT,
        CHECKPOINT,
        UNIT,
        { source: 'dynamic', key: 'checks', fieldType: 'enum', label: 'Inspection checklist' },
        { source: 'dynamic', key: 'work_log', fieldType: 'text', label: 'Maintenance log' },
        {
          source: 'dynamic',
          key: 'follow_up_required',
          fieldType: 'boolean',
          label: 'Follow-up required',
        },
        RECORDED_BY,
      ],
    },
    {
      ...baseReport(
        'Units needing follow-up',
        "Units whose most recent service record says they still need work. A unit drops off this list once it's serviced again without the follow-up box ticked.",
        formId,
      ),
      timeRange: { preset: 'all_time' },
      // Only each unit's latest record counts: this is "current state", not history.
      latestPerInstance: { enabled: true, n: 1 },
      dynamicFilters: [
        { fieldKey: 'follow_up_required', fieldType: 'boolean', operator: 'eq', value: true },
      ],
      selectedColumns: [
        CHECKPOINT,
        UNIT,
        CREATED_AT,
        { source: 'dynamic', key: 'follow_up_notes', fieldType: 'text', label: 'Still to do' },
        RECORDED_BY,
      ],
    },
  ],
};

export const SAMPLE_SCENARIOS: Record<SampleScenarioId, SampleScenarioDefinition> = {
  patrol: PATROL,
  janitorial: JANITORIAL,
  hvac: HVAC,
};

export function isSampleScenarioId(value: unknown): value is SampleScenarioId {
  return typeof value === 'string' && (SAMPLE_SCENARIO_IDS as readonly string[]).includes(value);
}
